import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { UserPlus, Users } from 'lucide-react'
import { addParticipant } from '@/utils/groupChatQueryOptions'
import type { GroupChatDetail } from '@/types/groupChatTypes'
import { RecipientField } from './RecipientField'
import { UnreachableNotice } from './UnreachableNotice'
import { useRecipientChips, type ResolvedRecipient } from './useRecipientChips'
import { canReceiveSMS, phoneKey } from '@/lib/phone'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

/** The Twilio ceiling on an SMS conversation, mirrored from the server. */
const MAX_PARTICIPANTS = 50

/** One person the add attempt refused, kept so the dialog can name them. */
interface Rejection {
  name: string
  reason: string
}

/**
 * Add people to a chat that is already running.
 *
 * Everyone is picked out of the directory rather than typed as a number, and
 * that is load-bearing: a new member reads the history in this app, and the
 * server matches them to the chat by Azure AD id or email. A bare phone number
 * would join the SMS conversation and still never see a word of what was said
 * before they arrived.
 *
 * Nothing is announced either way. The chat carries on, and the next message
 * anyone sends reaches the new members along with everybody else.
 */
export function AddPeopleDialog({
  chat,
  open,
  onOpenChange,
}: {
  chat: GroupChatDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const field = useRecipientChips()
  const [rejected, setRejected] = useState<Rejection[]>([])
  const [failure, setFailure] = useState<string | null>(null)

  // Who is in the chat already, by directory id and by number — the same person
  // can arrive through a group chip and an individual chip, or sit on the
  // roster under a differently formatted number.
  const present = useMemo(() => {
    const ids = new Set<string>()
    const phones = new Set<string>()
    for (const p of chat.participants) {
      if (p.azureAdUserId) ids.add(p.azureAdUserId)
      const key = phoneKey(p.phoneNumber)
      if (key) phones.add(key)
    }
    return { ids, phones }
  }, [chat.participants])

  const { toAdd, alreadyIn } = useMemo(() => {
    const isAlreadyIn = (r: ResolvedRecipient) =>
      (!!r.azureAdUserId && present.ids.has(r.azureAdUserId)) ||
      present.phones.has(phoneKey(r.phoneNumber))

    const reachable = field.recipients.filter((r) => canReceiveSMS(r.phoneNumber))
    return {
      toAdd: reachable.filter((r) => !isAlreadyIn(r)),
      alreadyIn: reachable.filter(isAlreadyIn),
    }
  }, [field.recipients, present])

  const room = Math.max(0, MAX_PARTICIPANTS - chat.participantCount)
  const overCapacity = toAdd.length > room

  const reset = () => {
    field.clear()
    setRejected([])
    setFailure(null)
  }

  const close = (o: boolean) => {
    if (!o) reset()
    onOpenChange(o)
  }

  const addMutation = useMutation({
    mutationFn: async () => {
      // One at a time, and on purpose. Twilio refuses a participant who is
      // still bound to another chat on this number, and the roster cap is
      // checked per add — fired in parallel, one refusal would tell you
      // nothing about who else got in.
      const refused: Rejection[] = []
      let added = 0
      for (const r of toAdd) {
        try {
          await addParticipant(chat.id, {
            phoneNumber: r.phoneNumber,
            displayName: r.displayName,
            azureAdUserId: r.azureAdUserId,
            email: r.email,
          })
          added++
        } catch (e) {
          refused.push({ name: r.displayName, reason: (e as Error).message })
        }
      }
      return { added, refused }
    },
    onSuccess: ({ added, refused }) => {
      if (added > 0) {
        queryClient.invalidateQueries({ queryKey: ['groupChat', chat.id] })
        queryClient.invalidateQueries({ queryKey: ['groupChats'] })
      }
      setFailure(null)
      setRejected(refused)
      // A clean run closes. Anyone refused stays on screen with the reason,
      // and the chips are cleared so a retry can't re-add whoever did get in.
      if (refused.length === 0) close(false)
      else field.clear()
    },
    onError: (e: Error) => setFailure(e.message),
  })

  const canAdd =
    toAdd.length > 0 && !overCapacity && !field.groupsResolving && !addMutation.isPending

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[88vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] bg-teal-soft text-teal">
              <UserPlus className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0">
              <DialogTitle>Add people to this chat</DialogTitle>
              <DialogDescription className="mt-0.5 text-[12px]">
                They can read everything already said in {chat.name}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <RecipientField field={field} label="Add" placeholder="Search groups or people…" />

          {field.hasChips && (
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-[7px] rounded-full bg-teal-soft px-[13px] py-1.5 text-[13px] font-bold text-foreground">
                <Users className="h-[13px] w-[13px] text-teal" />
                {toAdd.length} to add
                {field.groupsResolving && (
                  <span className="font-medium opacity-70">· resolving…</span>
                )}
              </span>
              <UnreachableNotice unreachable={field.unreachable} />
            </div>
          )}

          {/* Adding a whole group to top up a chat re-selects people who are
              already in it. That is expected, not a mistake — say so, rather
              than making someone hunt for the duplicates themselves. */}
          {alreadyIn.length > 0 && (
            <p className="text-[13px] text-muted-foreground">
              {alreadyIn.length === 1
                ? `${alreadyIn[0].displayName} is already in this chat and will be skipped.`
                : `${alreadyIn.length} of the people selected are already in this chat and will be skipped.`}
            </p>
          )}

          {overCapacity && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              A group chat holds {MAX_PARTICIPANTS} people. This one has {chat.participantCount}, so
              there is room for {room === 1 ? '1 more' : `${room} more`} — take{' '}
              {toAdd.length - room} off the list.
            </div>
          )}

          <div className="rounded-md bg-surface-2 px-3 py-2 text-[13px] text-muted-foreground">
            Each person added gets a text saying which chat they've joined. They see the whole
            thread here straight away, and their phone joins in from the next message anyone sends.
          </div>

          {rejected.length > 0 && (
            <div className="space-y-1.5 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <p className="font-semibold">
                {rejected.length === 1 ? 'One person was' : `${rejected.length} people were`} not
                added:
              </p>
              <ul className="space-y-1">
                {rejected.map((r) => (
                  <li key={r.name}>
                    <span className="font-medium">{r.name}</span> — {r.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {failure && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {failure}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>
            {rejected.length > 0 ? 'Close' : 'Cancel'}
          </Button>
          <Button onClick={() => addMutation.mutate()} disabled={!canAdd}>
            {addMutation.isPending
              ? 'Adding…'
              : toAdd.length === 1
                ? 'Add 1 person'
                : `Add ${toAdd.length} people`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
